import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';

import Button, { ButtonType } from 'components/Button';
import {
  WorkflowTypes,
  setActiveWorkflow,
  registerPlaidSuccessCallback,
  incrementConversationCount,
} from 'handlers/aiChat';

import { ReactComponent as CheckIcon } from 'images/green-check-rounded.svg';
import { ReactComponent as BackArrowIcon } from 'images/back-arrow-ai-chat.svg';
import AIThinkingLoader from 'components/FinancialCheckup/AICoach/AIThinkingLoader';

import { getAIChat } from 'selectors/aiChat';
import { getGoalOptimizer } from 'selectors/goalOptimizer';
import { getApplicationData } from 'selectors/getApplicationData';
import { getLoanGoal } from 'selectors/getLoanGoal';
import { getBankAccount } from 'selectors/getBankAccount';

import { explainGoal } from 'thunks';
import { AICoachGoalDecisionType } from 'enums/AICoach';
import { updateMessageByIndex, addTopic, removeDecisionAction, setLastAction, Action } from 'handlers/goalOptimizer';
import useBodyOverflow from 'hooks/useBodyOverflow';
import { useGoalActionHandler } from 'hooks/useGoalActionHandler';

import WelcomeMessage from 'components/FinancialCheckup/AICoach/WelcomeMessage';
import { aiCoachGoalsList } from 'components/AICoach/Goals/Goals';
import { filterValidGoals } from 'components/FinancialCheckup/FinancialDashboard/GoalsSection/useGoal';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';

import styles from './Chat.module.scss';

const GOAL_SELECTION_BUTTONS_THRESHOLD = 1;

const Chat = ({ scrollHeightRef }: { scrollHeightRef: React.RefObject<HTMLDivElement> }) => {
  const dispatch = useDispatch();
  const {
    messages,
    explanation,
    isLoading,
    recommendedPrimaryGoal,
    recommendedSecondaryGoal,
    topicsCovered,
    decisionActions,
  } = useSelector(getGoalOptimizer);
  const { aiCoachGoals, isLoading: isLoadingGoals } = useSelector(getLoanGoal);
  const { isLoading: isLoadingBankAccounts, savingsAccounts } = useSelector(getBankAccount);
  const [showActions, setShowActions] = useState<{ [key: number]: boolean }>({});
  const [actionItemsAnimatingOut, setActionItemsAnimatingOut] = useState<{ [key: number]: boolean }>({});
  const [animatingInLoader, setAnimatingInLoader] = useState<boolean>(false);
  const { application } = useSelector(getApplicationData);
  const { activeWorkflow, conversationCount } = useSelector(getAIChat);
  const titleRefs = React.useRef<{ [key: number]: HTMLHeadingElement | null }>({});
  const lastProcessedActionText = React.useRef<string | null>(null);
  const isLoadingChat = isLoading || isLoadingGoals || activeWorkflow?.isLoading || isLoadingBankAccounts;
  const isOfferAvailable = application!.status === ApplicationStatusName.OfferAvailable;

  const validGoals = filterValidGoals(aiCoachGoals, isOfferAvailable);

  const { handleDecisionAction, handleActionOverride } = useGoalActionHandler({
    application: application!,
    aiCoachGoals,
    decisionActions,
  });

  useBodyOverflow(true);

  useEffect(() => {
    if (explanation && explanation.actionText !== lastProcessedActionText.current && !isLoading) {
      // Mark this explanation as processed
      // The reducer of explainGoal.fulfilled will handle adding/updating the message with actions
      lastProcessedActionText.current = explanation.actionText;
    }
  }, [explanation, isLoading]);

  // Trigger action items animation when messages are loaded
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    let scrollTimer: NodeJS.Timeout | null = null;

    // Only show actions when isLoading is false and only for the last message
    if (!isLoading && messages.length > 0) {
      timer = setTimeout(() => {
        const newShowActions: { [key: number]: boolean } = {};
        // Only show actions for the last message
        const lastMessageIndex = messages.length - 1;
        newShowActions[lastMessageIndex] = true;
        setShowActions(newShowActions);
      }, 300); // Delay to show actions after message appears
    }

    // We don't want to scroll to bottom in the initial message
    if (messages.length === 1 && !isLoading) {
      return () => {
        if (timer) clearTimeout(timer);
      };
    }

    // Scroll to title after DOM updates
    scrollTimer = setTimeout(() => {
      // Find the last message with userResponse or successMessage
      let lastTitleIndex = -1;
      for (let i = messages.length - 1; i >= 0; i -= 1) {
        if (messages[i]?.userResponse || messages[i]?.successMessage) {
          lastTitleIndex = i;
          break;
        }
      }

      // If found a title, scroll to it
      if (lastTitleIndex !== -1) {
        const titleElement = titleRefs.current[lastTitleIndex];
        if (titleElement) {
          titleElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
          return;
        }
      }

      // If no title found, scroll to bottom as before
      scrollHeightRef.current?.scrollTo({
        top: scrollHeightRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }, 100);

    return () => {
      if (timer) clearTimeout(timer);
      if (scrollTimer) clearTimeout(scrollTimer);
    };
  }, [messages, isLoading]);

  useEffect(() => {
    // Stop the loader animation when the loading is done
    if (animatingInLoader && !isLoading && !activeWorkflow?.isLoading) {
      setAnimatingInLoader(false);
    }
  }, [isLoading, activeWorkflow?.isLoading]);

  useEffect(() => {
    if (isLoadingChat && !animatingInLoader) {
      setAnimatingInLoader(true);
    }
  }, [isLoadingChat]);

  const cleanActionsFromMessage = (messageIndex: number, text: string, remainingActions: Action[]) => {
    // Update message with userResponse and filtered actions (without the selected one)
    dispatch(
      updateMessageByIndex({
        index: messageIndex,
        message: {
          userResponse: text,
          actions: remainingActions,
        },
      }),
    );

    setActionItemsAnimatingOut((prev) => ({ ...prev, [messageIndex]: false }));
  };

  const handleBackClick = (messageIndex: number) => {
    const currentMessage = messages[messageIndex];
    if (currentMessage.previousActions) {
      // Restore previous actions and remove userResponse/showBackButton
      dispatch(
        updateMessageByIndex({
          index: messageIndex,
          message: {
            actions: currentMessage.previousActions,
            userResponse: undefined,
            showBackButton: false,
            previousActions: undefined,
          },
        }),
      );
    }
  };

  const handleActionClick = async (messageIndex: number, action: Action) => {
    const { actionText, isEducation, needsPlaid, sourceActionId } = action;

    const newCount = conversationCount + 1;
    dispatch(incrementConversationCount());
    analytics.track('AI Coach Option Selected', {
      option: actionText,
      conversationCount: newCount,
    });

    // Check if action has an override - if so, execute it and return early
    const handleOverrideResult = await handleActionOverride(action);
    if (handleOverrideResult) return;

    // Save the action as lastAction when clicked
    if (action.isDecision || action.isEducation || action.isUserChoice) {
      dispatch(setLastAction(action));
    }

    // Handle isUserChoice with multiple goals - show goal selection
    if (action.isUserChoice && validGoals.length > GOAL_SELECTION_BUTTONS_THRESHOLD) {
      // Start exit animation for current actions
      setActionItemsAnimatingOut((prev) => ({ ...prev, [messageIndex]: true }));

      // Save current actions before replacing
      const currentMessage = messages[messageIndex];
      const previousActions = [...currentMessage.actions];

      // After animation, replace actions with goal selection buttons
      setTimeout(() => {
        const goalActions: Action[] = validGoals.map((goal, idx) => {
          const goalTitle = aiCoachGoalsList.find((g) => g.value === goal.type)?.title!;

          return {
            id: Date.now() + idx,
            actionText: goal.description || goalTitle,
            isDecision: true,
            goals: [goal.type],
          };
        });

        dispatch(
          updateMessageByIndex({
            index: messageIndex,
            message: {
              actions: goalActions,
              userResponse: 'Please select the goal you want to focus on:',
              showBackButton: true,
              previousActions,
            },
          }),
        );

        setActionItemsAnimatingOut((prev) => ({ ...prev, [messageIndex]: false }));
        setShowActions((prev) => ({ ...prev, [messageIndex]: true }));
      }, 400);

      return;
    }

    // Handle "Add account" option - trigger Plaid workflow
    if (needsPlaid) {
      const fullScreenActive = savingsAccounts.length > 0 ? true : undefined;

      dispatch(
        setActiveWorkflow({
          success: false,
          type: WorkflowTypes.Plaid,
          isLoading: true,
          sourceActionId: sourceActionId ?? action.id,
          openPlaid: savingsAccounts.length === 0,
          fullScreen: fullScreenActive,
        }),
      );

      if (fullScreenActive) return;

      registerPlaidSuccessCallback(action.id, async () => {
        dispatch(
          setActiveWorkflow({
            type: WorkflowTypes.Plaid,
            success: true,
            isLoading: true,
            fullScreen: true,
          }),
        );
      });

      return;
    }

    // Handle decision actions: check if Plaid is needed or update goal directly
    if (action.isDecision) {
      const result = handleDecisionAction(action);
      if (result.handled && result.needsPlaid) {
        // Plaid workflow was triggered, don't remove message/actions yet
        return;
      }
    }

    // Start exit animation
    setActionItemsAnimatingOut((prev) => ({ ...prev, [messageIndex]: true }));

    // Get current message to filter actions
    const currentMessage = messages[messageIndex];
    const remainingActions = currentMessage.actions.filter((a) => a.actionText !== actionText);

    // After action item animation completes, update the message
    setTimeout(() => {
      cleanActionsFromMessage(messageIndex, actionText, remainingActions);
      setAnimatingInLoader(true);

      // If isEducation is true, call explain with topicsCovered
      if (isEducation) {
        // Add the actionText to topicsCovered
        dispatch(addTopic(actionText));

        // Call explain with current topicsCovered + the new topic
        // Use the updated array directly to ensure we include the new topic
        const updatedTopicsCovered = topicsCovered.includes(actionText)
          ? topicsCovered
          : [...topicsCovered, actionText];
        dispatch(
          explainGoal({
            actionText,
            applicationId: application?.id!,
            recommendedGoalType: recommendedPrimaryGoal ?? AICoachGoalDecisionType.ImproveCreditScore,
            recommendedGoalTypeSecondary: recommendedSecondaryGoal,
            topicsCovered: updatedTopicsCovered,
          }),
        );
      } else {
        // Decision actions are handled by useGoalActionHandler hook
        // If isDecision is true, remove it from decisionActions
        dispatch(removeDecisionAction(actionText));
      }
    }, 400);
  };

  return (
    <>
      {messages.length === 0 && (
        <p className={styles.explanation}>
          <WelcomeMessage firstName={application?.borrowerFirstName} showLoader={isLoading} />
        </p>
      )}
      {messages.map((message, index) => (
        <React.Fragment key={index}>
          <p className={styles.explanation}>
            {index === 0 && <WelcomeMessage firstName={application?.borrowerFirstName} />}
            {message.explanation}
          </p>
          {message.userResponse && !message.successMessage && (
            <h3
              ref={(el) => {
                titleRefs.current[index] = el;
              }}
              className={styles.chatTitle}
            >
              {message.userResponse}
              {message.showBackButton && index === messages.length - 1 && !isLoadingChat && (
                <div className={styles.backButton} onClick={() => handleBackClick(index)}>
                  <BackArrowIcon />
                </div>
              )}
            </h3>
          )}
          {message.successMessage && (
            <h3
              ref={(el) => {
                titleRefs.current[index] = el;
              }}
              className={styles.chatTitle}
            >
              <span className={styles.successMessage}>
                <CheckIcon className={styles.successIcon} />
                <span>
                  {message.successMessage}
                  <span className={styles.successMessageText}> is set</span>
                </span>
              </span>
            </h3>
          )}
          {isLoadingChat && index === messages.length - 1 && <AIThinkingLoader animating={animatingInLoader} />}
          {!isLoadingChat && index === messages.length - 1 && messages.length > 0 && (
            <div
              className={clsx(
                styles.optionsContainer,
                showActions[index] && styles.show,
                actionItemsAnimatingOut[index] && styles.hide,
              )}
            >
              {message.actions
                .filter((action) => {
                  // Hide isUserChoice actions when there are no valid goals to show
                  if (action.isUserChoice && validGoals.length === 0) return false;
                  return true;
                })
                .map((action, actionIndex) => (
                  <Button
                    key={action.id}
                    type={ButtonType.ChatOptionButton}
                    onClick={() => handleActionClick(index, action)}
                    className={styles.promptButton}
                    loaderSize={50}
                    style={{ '--index': actionIndex } as React.CSSProperties}
                  >
                    {action.actionText.charAt(0).toUpperCase() + action.actionText.slice(1).toLowerCase()}
                  </Button>
                ))}
            </div>
          )}
        </React.Fragment>
      ))}
    </>
  );
};

export default Chat;
